import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Compass, LogOut, Mail, MapPin, Menu, Moon, Shield, Sun, User, X,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { useTutorialStore } from '../../stores/tutorialStore';
import { useEinfuehrungStore } from '../../stores/einfuehrungStore';
import { useFreigabenStore } from '../../stores/freigabenStore';
import { useFeedbackStore } from '../../stores/feedbackStore';
import { SeitenHilfe, TutorialSchalter } from '../SeitenHilfe';
import { Einfuehrung } from '../Einfuehrung';
import { MeldenKnopf } from '../Melden/MeldenKnopf';
import { NAV_KATALOG, NAV_KATEGORIEN, PLATZ, navLabel, sichtbar, type NavEintrag } from '../../lib/nav';
import { istRedaktion, rollenText } from '../../lib/rollen';
import { sportschulenFuerStudios } from '../../lib/sportschulen';
import './Layout.css';

/**
 * Rahmen der Anwendung — aufgebaut wie im CRM und im Event Manager:
 * Kopfzeile, Seitenleiste mit Kategorien, Erklärkasten über jeder Seite.
 */
export function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { toggleTheme, isDark } = useThemeStore();
  const ladeTutorial = useTutorialStore((s) => s.laden);
  const ladeEinfuehrung = useEinfuehrungStore((s) => s.laden);
  const begruessungZeigen = useEinfuehrungStore((s) => s.begruessungZeigen);
  const freigabenAnzahl = useFreigabenStore((s) => s.anzahl);
  const ladeFreigaben = useFreigabenStore((s) => s.laden);
  const feedbackAnzahl = useFeedbackStore((s) => s.anzahl);
  const ladeFeedback = useFeedbackStore((s) => s.laden);

  const navigate = useNavigate();
  const location = useLocation();

  const [menuOffen, setMenuOffen] = useState(false);
  const [leisteOffen, setLeisteOffen] = useState(false); // nur mobil
  const menuRef = useRef<HTMLDivElement>(null);

  // Aufgeklappte Kategorien (lokal gemerkt; Standard: offen).
  const [offeneKat, setOffeneKat] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('mail-nav-open') || '{}'); } catch { return {}; }
  });
  const katUmschalten = (id: string) => setOffeneKat((vorher) => {
    const neu = { ...vorher, [id]: vorher[id] === false };
    try { localStorage.setItem('mail-nav-open', JSON.stringify(neu)); } catch { /* egal */ }
    return neu;
  });

  const userId = user?.id;
  const redaktion = istRedaktion(user);
  const teamEdit = user?.role === 'admin' || user?.role === 'redakteur';

  // Tutorial-Einstellung und Einführungs-Merker gehören zur Person und
  // werden einmal je Anmeldung geholt.
  useEffect(() => {
    if (!userId) return;
    void ladeTutorial();
    void ladeEinfuehrung();
  }, [userId, ladeTutorial, ladeEinfuehrung]);

  // Nach jedem Seitenwechsel neu zählen: Wer unter Freigaben entscheidet und
  // dann weiterklickt, soll keine veraltete Zahl sehen.
  useEffect(() => {
    if (userId && redaktion) void ladeFreigaben();
  }, [location.pathname, userId, redaktion, ladeFreigaben]);

  // Neue Rückmeldungen von Ansprechpersonen: sieht nur das Team, das sie einarbeitet.
  useEffect(() => {
    if (userId && teamEdit) void ladeFeedback();
  }, [location.pathname, userId, teamEdit, ladeFeedback]);

  useEffect(() => {
    if (!menuOffen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOffen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOffen]);

  // Beim Seitenwechsel die (mobile) Seitenleiste schließen.
  useEffect(() => { setLeisteOffen(false); }, [location.pathname]);

  if (!user) return null;

  const abmelden = async () => {
    setMenuOffen(false);
    await logout();
    navigate('/login');
  };

  const name = user.fullName || user.email;
  const rolle = rollenText(user);
  const schulen = sportschulenFuerStudios(user.studios).map((s) => s.name);

  const eintrag = (it: NavEintrag) => (
    <NavLink
      key={it.key}
      to={it.path}
      end={it.end}
      data-tour={`nav-${it.key}`}
      className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
    >
      <it.Icon size={16} />
      <span className="sidebar-label">{navLabel(it, user)}</span>
      {it.key === 'freigaben' && freigabenAnzahl > 0 && (
        <span className="sidebar-zahl" aria-label={`${freigabenAnzahl} warten auf Freigabe`}>
          {freigabenAnzahl > 99 ? '99+' : freigabenAnzahl}
        </span>
      )}
      {it.key === 'geplante-mails' && teamEdit && feedbackAnzahl > 0 && (
        <span className="sidebar-zahl" aria-label={`${feedbackAnzahl} neue Rückmeldungen`}>
          {feedbackAnzahl > 99 ? '99+' : feedbackAnzahl}
        </span>
      )}
    </NavLink>
  );

  const nachKat: Record<string, NavEintrag[]> = {};
  for (const it of NAV_KATALOG) {
    if (!sichtbar(it, user)) continue;
    (nachKat[PLATZ[it.key] ?? 'uebersicht'] ??= []).push(it);
  }

  return (
    <div className={`layout sidebar-mode${leisteOffen ? ' sidebar-open' : ''}`}>
      <header className="header">
        <div className="header-left">
          <button
            className="sidebar-burger"
            onClick={() => setLeisteOffen((v) => !v)}
            aria-label="Menü"
            aria-expanded={leisteOffen}
          >
            {leisteOffen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Link to="/" className="logo" aria-label="Randori Mail – zur Startseite">
            <Mail className="logo-icon" aria-hidden="true" />
            <span className="logo-text">Randori Mail</span>
          </Link>
        </div>

        <div className="header-right">
          {/* Sichtbarer Einstieg in den Rundgang — jederzeit und beliebig oft. */}
          <button
            className="header-btn"
            data-tour="kopf-einfuehrung"
            onClick={begruessungZeigen}
            title="Einführung starten"
            aria-label="Einführung starten"
          >
            <Compass size={18} />
            <span className="header-btn-text">Einführung</span>
          </button>

          <TutorialSchalter />

          {/* Fehlt etwas oder hakt es? Direkt als Ticket ins Ticketsystem. */}
          <MeldenKnopf rolle={rolle} />

          <button
            className="theme-toggle"
            data-tour="kopf-thema"
            onClick={toggleTheme}
            title={isDark() ? 'Heller Modus' : 'Dunkler Modus'}
            aria-label={isDark() ? 'Heller Modus' : 'Dunkler Modus'}
          >
            {isDark() ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <div className="user-info-wrapper" ref={menuRef}>
            <button
              className="user-info"
              onClick={() => setMenuOffen((v) => !v)}
              aria-expanded={menuOffen}
              aria-haspopup="menu"
              aria-label={`Benutzermenü ${name}`}
            >
              <div className="user-avatar">
                {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <User size={20} />}
              </div>
              <div className="user-details">
                <span className="user-name">{name}</span>
                <span className="user-role">{rolle}</span>
              </div>
              <ChevronDown size={15} className={`user-chevron${menuOffen ? ' rotated' : ''}`} />
            </button>

            {menuOffen && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <div className="user-dropdown-avatar">
                    {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <User size={22} />}
                  </div>
                  <div>
                    <div className="user-dropdown-name">{name}</div>
                    <div className="user-dropdown-email">{user.email}</div>
                  </div>
                </div>
                <div className="user-dropdown-role">
                  <Shield size={13} />
                  <span>{rolle}{user.quelle === 'crm' ? ' · aus dem CRM' : ''}</span>
                </div>
                {schulen.length > 0 && (
                  <div className="user-dropdown-role">
                    <MapPin size={13} />
                    <span>{schulen.join(', ')}</span>
                  </div>
                )}
                <div className="user-dropdown-divider" />
                <button className="user-dropdown-logout" onClick={abmelden}>
                  <LogOut size={14} />
                  <span>Abmelden</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {leisteOffen && <div className="sidebar-overlay" onClick={() => setLeisteOffen(false)} />}

      <aside className="sidebar" data-tour="seitenleiste">
        <nav className="sidebar-nav" aria-label="Hauptnavigation">
          {NAV_KATEGORIEN.map((kat) => {
            const items = nachKat[kat.id];
            if (!items || items.length === 0) return null;
            const offen = offeneKat[kat.id] !== false;
            return (
              <div key={kat.id} className="sidebar-group">
                <button
                  type="button"
                  className="sidebar-group-label sidebar-group-toggle"
                  onClick={() => katUmschalten(kat.id)}
                  aria-expanded={offen}
                >
                  {offen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span>{kat.label}</span>
                </button>
                {offen && items.map(eintrag)}
              </div>
            );
          })}
        </nav>
      </aside>

      <main className="main-content">
        {/* Erklärkasten der jeweiligen Seite — findet seinen Text selbst. */}
        <SeitenHilfe key={location.pathname.split('/')[1] ?? ''} />
        <Outlet />
      </main>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} Randori-Pro Management GmbH · Randori Mail</p>
      </footer>

      {/* Begrüßung beim ersten Mal und die Touren. */}
      <Einfuehrung />
    </div>
  );
}
